import { useCallback, useEffect, useState } from 'react';
import { SECTION_IDS } from '@/types';
import type { SectionId } from '@/types';

const DEFAULT_SECTION: SectionId = 'home';

function isSectionId(value: string): value is SectionId {
  return (SECTION_IDS as readonly string[]).includes(value);
}

/** Parse `#/tools` (or the legacy `#tools`) into a section id. */
export function sectionFromHash(hash: string): SectionId {
  const candidate = hash.replace(/^#\/?/, '').split('?')[0] ?? '';
  return isSectionId(candidate) ? candidate : DEFAULT_SECTION;
}

export function hashForSection(section: SectionId): string {
  return `#/${section}`;
}

/**
 * Hash-based routing.
 *
 * Hash routes keep every section linkable and make the browser's back button
 * work, while still deploying as plain static files with no server rewrite
 * rules — the right trade-off for a site hosted on GitHub Pages or similar.
 */
export function useHashRoute(): {
  section: SectionId;
  navigate: (section: SectionId) => void;
} {
  const [section, setSection] = useState<SectionId>(() =>
    typeof window === 'undefined' ? DEFAULT_SECTION : sectionFromHash(window.location.hash),
  );

  useEffect(() => {
    const syncFromUrl = () => setSection(sectionFromHash(window.location.hash));
    window.addEventListener('hashchange', syncFromUrl);

    // Normalise the hash so the URL always names the view in canonical form —
    // empty, unknown (`#/bogus`) and legacy (`#tools`) all included. Leaving a
    // non-canonical hash in place means the next click on that same section
    // sees a differing target and pushes a history entry for a view that does
    // not change, costing the visitor an extra Back press.
    const canonical = hashForSection(sectionFromHash(window.location.hash));
    if (window.location.hash !== canonical) {
      window.history.replaceState(null, '', canonical);
    }

    // Re-read the URL after normalising. The hash can change between the first
    // render and this effect — a deep link arriving mid-load, or a restore from
    // the back/forward cache — and without this resync the address bar and the
    // rendered view can disagree.
    syncFromUrl();

    return () => window.removeEventListener('hashchange', syncFromUrl);
  }, []);

  const navigate = useCallback((next: SectionId) => {
    const target = hashForSection(next);

    // Update state eagerly rather than waiting for `hashchange`, which browsers
    // dispatch asynchronously — otherwise the view lags a frame behind the URL.
    // The listener still fires afterwards and is a no-op once state matches.
    setSection(next);

    if (window.location.hash !== target) {
      window.location.hash = target;
    }
  }, []);

  return { section, navigate };
}
